import React, { useId } from 'react';

/* ═══════════════════════════════════════════════════════════════════
   WAX SEAL — realistic royal wax seal with poured drips,
   embossed metal stamp ring, decorative star pattern, and a
   golden signature impression auto-fitted to the centre.
   ═══════════════════════════════════════════════════════════════════ */

const WAX_LIGHT = '#d8252a';
const WAX = '#9b1414';
const WAX_DARK = '#3d0707';
const WAX_DEEP = '#1f0303';
const GOLD = '#D4AF37';
const GOLD_LIGHT = '#F5D97A';

/* Build an irregular blob-shape clip-path so the wax has hand-poured drips. */
const DRIP_PATH = (() => {
  const points = 22;
  const pts = [];
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * Math.PI * 2;
    // Irregular radius — random-feeling but seeded so it stays consistent
    const noise = [
      0.94, 1.02, 0.97, 1.05, 0.92, 1.04, 0.99, 1.06,
      0.95, 1.02, 0.93, 1.07, 0.96, 1.01, 0.94, 1.05,
      0.98, 1.03, 0.92, 1.04, 0.97, 1.02,
    ][i] || 1;
    const r = 50 * noise;
    const x = 50 + Math.cos(angle) * r;
    const y = 50 + Math.sin(angle) * r;
    pts.push(`${x.toFixed(2)}% ${y.toFixed(2)}%`);
  }
  return `polygon(${pts.join(', ')})`;
})();

export const WaxSeal = ({
  signatureUrl,
  size = 90,
  pulse = false,
  onClick,
  testId = 'wax-seal',
}) => {
  // Unique ids so multiple seals on the same page don't clash
  const idBase = useId().replace(/:/g, '');
  const waxGradId = `wax-${idBase}`;
  const ridgeGradId = `ridge-${idBase}`;
  const goldGradId = `gold-${idBase}`;

  // Inner stamp area is ~64% of seal — signature lives here
  const stampPct = 64;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`relative inline-flex items-center justify-center ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      style={{
        width: size,
        height: size,
        background: 'transparent',
        border: 'none',
        padding: 0,
        animation: pulse ? 'waxPulse 2.4s ease-in-out infinite' : 'none',
        filter: 'drop-shadow(0 5px 12px rgba(0,0,0,0.55))',
      }}
      aria-label="Wax seal"
      data-testid={testId}
    >
      {/* Wax body — irregular drip outline */}
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          clipPath: DRIP_PATH,
          background: `
            radial-gradient(circle at 32% 28%, ${WAX_LIGHT} 0%, ${WAX} 38%, ${WAX_DARK} 78%, ${WAX_DEEP} 100%)
          `,
          boxShadow: 'inset 0 -4px 8px rgba(0,0,0,0.55)',
        }}
      />

      {/* Subtle wax sheen — bright highlight on the top-left */}
      <span
        aria-hidden
        className="absolute"
        style={{
          inset: '8%',
          clipPath: DRIP_PATH,
          background:
            'radial-gradient(ellipse 55% 35% at 32% 22%, rgba(255,200,200,0.55) 0%, rgba(255,200,200,0) 60%)',
          mixBlendMode: 'screen',
          opacity: 0.85,
        }}
      />

      {/* Embossed stamp ridge — the metal stamp pressed into the wax */}
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: `${stampPct + 8}%`,
          height: `${stampPct + 8}%`,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          border: `${Math.max(1, size * 0.018)}px solid rgba(60,5,5,0.55)`,
          boxShadow:
            'inset 0 1px 2px rgba(255,180,180,0.35), inset 0 -2px 4px rgba(0,0,0,0.55)',
        }}
      />      {/* Decorative royal stars around the inner ridge */}
      <svg
        aria-hidden
        className="absolute"
        style={{ inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        viewBox="0 0 100 100"
      >
        <defs>
          <radialGradient id={waxGradId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFD18A" />
            <stop offset="60%" stopColor={GOLD} />
            <stop offset="100%" stopColor="#7a5b0f" />
          </radialGradient>
          <linearGradient id={ridgeGradId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,200,200,0.7)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.55)" />
          </linearGradient>
          <linearGradient id={goldGradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={GOLD_LIGHT} />
            <stop offset="100%" stopColor={GOLD} />
          </linearGradient>
        </defs>

        {/* Eight tiny royal dots around the inner ring */}
        {Array.from({ length: 8 }).map((_, i) => {
          const ang = (i / 8) * Math.PI * 2 - Math.PI / 2;
          const r = (stampPct + 8) / 2 + 0.5; // sit ON the ridge
          const cx = 50 + Math.cos(ang) * r;
          const cy = 50 + Math.sin(ang) * r;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={1.1}
              fill={`url(#${waxGradId})`}
              opacity={0.85}
            />
          );
        })}
      </svg>

      {/* Inner stamp face — slightly recessed parchment-tinted area */}
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: `${stampPct}%`,
          height: `${stampPct}%`,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          background:
            `radial-gradient(circle at 50% 50%, rgba(120,15,15,0.55) 0%, rgba(60,5,5,0.85) 100%)`,
          boxShadow:
            'inset 0 2px 4px rgba(0,0,0,0.7), inset 0 -1px 2px rgba(255,160,160,0.25)',
        }}
      />

      {/* Signature impression — auto-fits within the inner stamp area */}
      <span
        className="absolute flex items-center justify-center pointer-events-none select-none"
        style={{
          width: `${stampPct - 4}%`,
          height: `${stampPct - 4}%`,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '6%',
          zIndex: 2,
        }}
      >
        {signatureUrl ? (
          <img
            src={signatureUrl}
            alt="Royal signature"
            className="w-full h-full"
            style={{
              objectFit: 'contain',
              // Convert dark ink to luminous gold and add a soft engraved glow
              filter:
                'invert(1) sepia(1) saturate(7) hue-rotate(-12deg) brightness(1.05) drop-shadow(0 0 1.5px rgba(0,0,0,0.6)) drop-shadow(0 0 2.5px rgba(255,221,140,0.7))',
              opacity: 0.95,
            }}
            draggable={false}
          />
        ) : (
          <span
            className="font-['Cormorant_Garamond'] font-bold leading-none"
            style={{
              color: GOLD_LIGHT,
              fontSize: size * 0.42,
              textShadow:
                '0 1px 0 rgba(0,0,0,0.7), 0 0 6px rgba(255,221,140,0.55)',
              letterSpacing: '0.03em',
            }}
          >
            B
          </span>
        )}
      </span>

      <style>{`
        @keyframes waxPulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.05); }
        }
      `}</style>
    </button>
  );
};

export default WaxSeal;
